import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  showSessionTimeout, showSessionWarning,
  isSessionExpirationRecent, clearStaleSessionTimeout, setCredentials, logout
} from '../../slices/authSlice';
import { proactiveRefresh } from '../../services/apiService';

const TOKEN_TTL_MS = 3 * 60 * 60 * 1000;
const PROACTIVE_REFRESH_THRESHOLD_MS = 30 * 60 * 1000;
const WARNING_THRESHOLD_MS = 12 * 60 * 1000;
const IDLE_THRESHOLD_MS = 10 * 60 * 1000;
const CHECK_INTERVAL_MS = 60 * 1000;

const SESSION_TIMEOUT_MODAL_KEY = 'sessionTimeoutModalShown';
// Cross-tab session sync: a tab's activity and token refresh are shared via localStorage so a
// backgrounded tab does not falsely time out while another tab keeps the (shared) session alive.
const LAST_ACTIVITY_KEY = 'lastActivityAt';
const ACTIVITY_PERSIST_THROTTLE_MS = 5 * 1000;

const readStoredNumber = (key) => {
  try {
    const parsed = parseInt(localStorage.getItem(key) || '', 10);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const AuthTimeoutManager = () => {
  const dispatch = useDispatch();
  const { user, loginTimestamp, tokenExpiresAt } = useSelector((state) => state.auth);
  const lastActivityRef = useRef(Date.now());
  const lastStoredActivityRef = useRef(0);
  const intervalRef = useRef(null);
  const warningShownRef = useRef(false);

  const updateActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    // Persist (throttled) so activity in ANY tab counts toward every tab's idle calculation.
    if (now - lastStoredActivityRef.current >= ACTIVITY_PERSIST_THROTTLE_MS) {
      lastStoredActivityRef.current = now;
      try { localStorage.setItem(LAST_ACTIVITY_KEY, String(now)); } catch { /* ignore quota */ }
    }
  }, []);

  // Mount-only: restore stale session timeout modal flag (same logic as before)
  useEffect(() => {
    if (window.location.pathname === '/login' || window.location.pathname.startsWith('/login/')) {
      clearStaleSessionTimeout();
      return;
    }

    const sessionTimeoutModalShown = localStorage.getItem(SESSION_TIMEOUT_MODAL_KEY) === 'true';
    if (sessionTimeoutModalShown) {
      if (isSessionExpirationRecent()) {
        dispatch(showSessionTimeout());
      } else {
        clearStaleSessionTimeout();
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user || !loginTimestamp) {
      clearInterval(intervalRef.current);
      return;
    }

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, updateActivity, { passive: true }));
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') updateActivity();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // Cross-tab sync: adopt another tab's token refresh, mirror a genuine timeout, sync logout.
    const handleStorage = (event) => {
      if (event.key === 'tokenExpiresAt' || event.key === 'loginTimestamp') {
        const syncedLoginTimestamp = readStoredNumber('loginTimestamp');
        const syncedTokenExpiresAt = readStoredNumber('tokenExpiresAt');
        if (syncedLoginTimestamp || syncedTokenExpiresAt) {
          dispatch(setCredentials({
            ...(syncedLoginTimestamp ? { loginTimestamp: syncedLoginTimestamp } : {}),
            ...(syncedTokenExpiresAt ? { tokenExpiresAt: syncedTokenExpiresAt } : {})
          }));
          warningShownRef.current = false; // a refresh elsewhere clears a stale warning here
        }
      } else if (event.key === SESSION_TIMEOUT_MODAL_KEY && event.newValue === 'true') {
        dispatch(showSessionTimeout()); // genuine timeout in another tab -> mirror (singleton-guarded)
      } else if (event.key === 'user' && !event.newValue) {
        dispatch(logout()); // logout in another tab -> stay consistent
      }
    };
    window.addEventListener('storage', handleStorage);

    intervalRef.current = setInterval(async () => {
      const now = Date.now();
      // Read the shared (localStorage) expiry first: whichever tab last refreshed wins, so a
      // background tab never acts on a stale Redux expiry.
      const effectiveLoginTimestamp = readStoredNumber('loginTimestamp') || loginTimestamp;
      const expiresAt = readStoredNumber('tokenExpiresAt') || tokenExpiresAt || (effectiveLoginTimestamp + TOKEN_TTL_MS);
      const timeUntilExpiry = expiresAt - now;
      // Idle = no activity in ANY tab (shared last-activity), not just this one.
      const storedActivity = readStoredNumber(LAST_ACTIVITY_KEY) || 0;
      const isIdle = (now - Math.max(lastActivityRef.current, storedActivity)) >= IDLE_THRESHOLD_MS;

      // Token already expired — nudge idle user with warning so they know
      if (timeUntilExpiry <= 0) {
        if (!warningShownRef.current) {
          warningShownRef.current = true;
          dispatch(showSessionWarning());
        }
        return;
      }

      // Active user + token expiring soon → silent refresh
      if (timeUntilExpiry < PROACTIVE_REFRESH_THRESHOLD_MS && !isIdle) {
        const ok = await proactiveRefresh();
        if (ok) {
          const newTimestamp = Date.now();
          const newExpiresAt = newTimestamp + TOKEN_TTL_MS;
          dispatch(setCredentials({ loginTimestamp: newTimestamp, tokenExpiresAt: newExpiresAt }));
          localStorage.setItem('loginTimestamp', newTimestamp.toString());
          localStorage.setItem('tokenExpiresAt', newExpiresAt.toString());
          warningShownRef.current = false;
        }
        return;
      }

      // Idle user + token about to expire → show warning
      if (timeUntilExpiry < WARNING_THRESHOLD_MS && isIdle && !warningShownRef.current) {
        warningShownRef.current = true;
        dispatch(showSessionWarning());
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      clearInterval(intervalRef.current);
      events.forEach(e => window.removeEventListener(e, updateActivity));
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('storage', handleStorage);
    };
  }, [user, loginTimestamp, tokenExpiresAt, dispatch, updateActivity]);

  return null;
};

export default AuthTimeoutManager;
